'use client';

import { useState } from 'react';
import { useResetOnHide } from '@/hooks/useResetOnHide';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/useToast';
import { PlusIcon, Trash2, UserMinus } from 'lucide-react';
import { addUserToTeam, removeUserFromTeam } from '@/apis/addAndRemoveFromTeam';
import User from '@/types/userType';
import { deleteTeam } from '@/apis/deleteTeam';
import posthog from '@/lib/posthog';
import useUserContextStore from '@/contexts/UserContext';
import { isAdminOrOwner } from '@/lib/rbac';

function TeamMembersModalTitleBar({
  title,
  onClose,
}: {
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="flex justify-between items-center">
      <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
      <button
        type="button"
        className="cursor-pointer hover:opacity-80 p-0 bg-transparent border-none text-ds-text-default min-w-0 h-auto"
        onClick={onClose}
        aria-label="Close"
      >
        ✕
      </button>
    </div>
  );
}

interface TeamMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: TeamTypeWithMembers | null;
  orgArr: User[];
  setTeams: React.Dispatch<React.SetStateAction<TeamTypeWithMembers[]>>;
  setSelectedTeam: React.Dispatch<
    React.SetStateAction<TeamTypeWithMembers | null>
  >;
}

export const TeamMembersModal = ({
  isOpen,
  onClose,
  team,
  orgArr,
  setTeams,
  setSelectedTeam,
}: TeamMembersModalProps) => {
  const toast = useToast();
  const [selectedUser, setSelectedUser] = useState<User>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  useResetOnHide(setIsDropdownOpen);
  const currentUser = useUserContextStore((s) => s.user);
  const isAdmin = isAdminOrOwner(currentUser?.Role);

  // Filter and sort users based on search query
  const filteredAndSortedUsers = orgArr
    .filter((user) => {
      const username = user.GithubUsername || user.GitlabUsername || '';
      const displayName =
        user.FirstName &&
        user.FirstName !== 'null' &&
        user.FirstName !== 'undefined' &&
        user.FirstName !== user.GithubUsername
          ? `${user.FirstName} ${user.LastName && user.LastName !== 'null' && user.LastName !== 'undefined' ? user.LastName : ''}`.trim()
          : username;
      const isNotInTeam = !team?.TeamMembers.find(
        (member) => member.login === username
      );
      const matchesSearch =
        displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        username.toLowerCase().includes(searchQuery.toLowerCase());
      return isNotInTeam && username && matchesSearch;
    })
    .sort((a, b) => {
      const nameA = (
        a.FirstName &&
        a.FirstName !== 'null' &&
        a.FirstName !== 'undefined' &&
        a.FirstName !== a.GithubUsername
          ? `${a.FirstName} ${a.LastName && a.LastName !== 'null' && a.LastName !== 'undefined' ? a.LastName : ''}`.trim()
          : a.GithubUsername || a.GitlabUsername || ''
      ).toLowerCase();
      const nameB = (
        b.FirstName &&
        b.FirstName !== 'null' &&
        b.FirstName !== 'undefined' &&
        b.FirstName !== b.GithubUsername
          ? `${b.FirstName} ${b.LastName && b.LastName !== 'null' && b.LastName !== 'undefined' ? b.LastName : ''}`.trim()
          : b.GithubUsername || b.GitlabUsername || ''
      ).toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const deleteSelectedTeam = async () => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can delete teams',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    posthog.capture('team_deleted', {
      team_name: team?.TeamName,
      team_uuid: team?.TeamUUID,
      member_count: team?.TeamMembers.length,
      github_username: selectedUser?.GithubUsername,
    });

    const response = await deleteTeam(team?.TeamUUID || '');
    if (response?.Error) {
      toast({
        title: 'Error',
        description: response.Error,
        status: 'error',
        duration: 2000,
      });
    } else {
      toast({
        title: 'Team deleted Successfully',
        status: 'success',
        duration: 2000,
      });
      setTeams((prevTeams: TeamTypeWithMembers[]) => {
        return prevTeams.filter(
          (t: TeamTypeWithMembers) => t.TeamUUID !== team?.TeamUUID
        );
      });
      onClose();
      setSelectedTeam(null);
    }
  };

  const removeMemberFromTeam = async (
    userId: string,
    gitProviderUsername: string
  ) => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can remove members from teams',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    posthog.capture('team_member_removed', {
      team_name: team?.TeamName,
      team_uuid: team?.TeamUUID,
      member_github_username: gitProviderUsername,
      github_username: selectedUser?.GithubUsername,
    });

    try {
      await removeUserFromTeam(team?.TeamUUID || '', userId);

      toast({
        title: 'Member removed from team',
        status: 'success',
        duration: 2000,
      });

      setTeams((prevTeams: TeamTypeWithMembers[]) => {
        return prevTeams.map((t: TeamTypeWithMembers) => {
          if (t.TeamName === team?.TeamName) {
            const updatedMembers = t.TeamMembers.filter(
              (member) => member.login !== gitProviderUsername
            );
            return {
              ...t,
              TeamMembers: updatedMembers,
            };
          }
          return t;
        });
      });

      setSelectedTeam((currTeam: TeamTypeWithMembers | null) => {
        if (!currTeam) return null;
        return {
          ...currTeam,
          TeamMembers: currTeam.TeamMembers.filter(
            (member) => member.login !== gitProviderUsername
          ),
        };
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to remove member from team',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const addMemberToTeam = async (selectedUser: User | undefined) => {
    if (!selectedUser) return;
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can add users to teams',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    posthog.capture('team_member_added', {
      team_name: team?.TeamName,
      team_uuid: team?.TeamUUID,
      member_github_username:
        selectedUser.GithubUsername || selectedUser.GitlabUsername,
      github_username: selectedUser?.GithubUsername,
    });

    try {
      await addUserToTeam(team?.TeamUUID || '', selectedUser.UserUUID);

      toast({
        title: 'User added to team',
        status: 'success',
        duration: 2000,
      });

      setTeams((prevTeams: TeamTypeWithMembers[]) => {
        return prevTeams.map((t: TeamTypeWithMembers) => {
          if (t.TeamName === team?.TeamName) {
            return {
              ...t,
              TeamMembers: [
                ...t.TeamMembers,
                {
                  login:
                    selectedUser.GithubUsername ||
                    selectedUser.GitlabUsername ||
                    '',
                  avatar_url: selectedUser.Image || '',
                  name:
                    selectedUser.FirstName ||
                    selectedUser.GithubUsername ||
                    selectedUser.GitlabUsername ||
                    '',
                },
              ],
            };
          } else {
            return {
              ...t,
              TeamMembers: t.TeamMembers.filter(
                (member) =>
                  member.login !== selectedUser.GithubUsername &&
                  member.login !== selectedUser.GitlabUsername
              ),
            };
          }
        });
      });

      setSelectedTeam((currTeam: TeamTypeWithMembers | null) => {
        if (!currTeam) return null;
        return {
          ...currTeam,
          TeamMembers: [
            ...currTeam.TeamMembers,
            {
              login:
                selectedUser.GithubUsername ||
                selectedUser.GitlabUsername ||
                '',
              avatar_url: selectedUser.Image || '',
              name:
                selectedUser.FirstName ||
                selectedUser.GithubUsername ||
                selectedUser.GitlabUsername ||
                '',
            },
          ],
        };
      });
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to add user to team',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setSelectedUser(undefined);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="text-ds-text-default max-w-xl p-6 rounded-none [&>button]:hidden">
        <DialogHeader>
          <TeamMembersModalTitleBar
            title={`${team?.TeamName ?? ''} Members`}
            onClose={onClose}
          />
        </DialogHeader>

        <div className="overflow-y-auto max-h-[300px] my-10 space-y-2">
          {team?.TeamMembers.map((member) => {
            const matchedOrgUser = orgArr.find(
              (user) =>
                user.GithubUsername === member.login ||
                user.GitlabUsername === member.login
            );
            return (
              <div
                className="flex justify-between items-center"
                key={member.login}
              >
                <div className="flex items-center gap-2">
                  <Avatar className="size-8">
                    <AvatarImage src={member.avatar_url} alt={member.login} />
                  </Avatar>
                  <span className="text-ds-text-default">{member.login}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-ds-danger-default hover:text-ds-danger-default"
                    disabled={!isAdmin}
                    title={
                      !isAdmin
                        ? 'Only administrators can remove team members'
                        : ''
                    }
                    onClick={() => {
                      removeMemberFromTeam(
                        matchedOrgUser?.UserUUID || '',
                        member.login
                      );
                    }}
                  >
                    <UserMinus className="size-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          {team?.TeamMembers.length === 0 && (
            <p className="text-ds-text-subtle text-sm">
              No members in this team, select users to add
            </p>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex gap-4 justify-between mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full px-3 py-2 bg-ds-surface-white rounded-md text-ds-text-darker border border-ds-border-subtler focus:outline-none focus:border-ds-brand-subtler"
              />
              {isDropdownOpen && filteredAndSortedUsers.length > 0 && (
                <div className="absolute w-full mt-1 bg-ds-surface-white border border-ds-border-subtler rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                  {filteredAndSortedUsers.map((user) => (
                    <button
                      type="button"
                      key={user.UserUUID}
                      onClick={() => {
                        setSelectedUser(user);
                        setSearchQuery(
                          user.FirstName &&
                            user.FirstName !== 'null' &&
                            user.FirstName !== 'undefined' &&
                            user.FirstName !== user.GithubUsername
                            ? `${user.FirstName} ${user.LastName && user.LastName !== 'null' && user.LastName !== 'undefined' ? user.LastName : ''}`.trim()
                            : user.GithubUsername || user.GitlabUsername || ''
                        );
                        setIsDropdownOpen(false);
                      }}
                      className="w-full px-3 py-2 hover:bg-primitive-gray-50 cursor-pointer flex items-center gap-2 text-left"
                    >
                      <Avatar className="size-8">
                        <AvatarImage src={user.Image} alt="" />
                      </Avatar>
                      <div>
                        {user.FirstName &&
                        user.FirstName !== 'null' &&
                        user.FirstName !== 'undefined' &&
                        user.FirstName !== user.GithubUsername ? (
                          <>
                            <span className="block">
                              {`${user.FirstName} ${user.LastName && user.LastName !== 'null' && user.LastName !== 'undefined' ? user.LastName : ''}`.trim()}
                            </span>
                            <span className="text-sm text-ds-text-subtle block">
                              {user.GithubUsername || user.GitlabUsername}
                            </span>
                          </>
                        ) : (
                          <span>
                            {user.GithubUsername || user.GitlabUsername}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              className="px-9"
              disabled={!selectedUser || !isAdmin}
              title={
                !isAdmin ? 'Only administrators can add users to teams' : ''
              }
              onClick={() => {
                addMemberToTeam(selectedUser);
                setSearchQuery('');
                setSelectedUser(undefined);
              }}
            >
              <span className="flex items-center gap-2">
                <PlusIcon className="size-4" />
                Add User
              </span>
            </Button>
          </div>

          <Button
            className="w-full mt-4"
            onClick={deleteSelectedTeam}
            disabled={!isAdmin}
            title={!isAdmin ? 'Only administrators can delete teams' : ''}
          >
            Delete Team{' '}
            <Trash2 className="text-ds-danger-default ml-2 size-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
